import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserMenu } from './UserMenu';
import { CompactEditMenu } from './CompactEditMenu';
import { AutoFitTitle } from './AutoFitTitle';
import { useAuth } from '../auth/AuthorizerContext';
import { useIsEditLayoutNarrowViewport } from '../ui/useIsNarrowViewport';
import { ControlBarInteractor } from './ControlBarInteractor';
import { MenuInteractor } from './MenuInteractor';

interface ControlBarProps {
  isDraft?: boolean;
  onSave?: () => void;
  showSaveButton?: boolean;
  isEditMode?: boolean;
  treeId?: string;
  isSaving?: boolean;
  isLoading?: boolean;
  canEdit?: boolean;
  treeName?: string;
  onRenameTree?: (name: string) => void;
  /**
   * The editor's DRAFT/undo/redo cluster, built and gated by the page (TreeEdit).
   * Rendered on the wide toolbar only — on narrow viewports the same cluster moves
   * to the dev panel bar so the compact tree title keeps its space.
   */
  editStatus?: React.ReactNode;
  /** Download this tree as a deployable artifact. Edit page only; see DeployInteractor. */
  onDeploy?: () => void;
  isDeploying?: boolean;
}

export const ControlBar: React.FC<ControlBarProps> = ({
  isDraft = false,
  onSave,
  showSaveButton = false,
  isEditMode = false,
  treeId,
  isSaving = false,
  isLoading = false,
  canEdit = false,
  treeName = '',
  onRenameTree,
  editStatus,
  onDeploy,
  isDeploying = false,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNarrowViewport = useIsEditLayoutNarrowViewport();
  const controlBarInteractor = useMemo(() => new ControlBarInteractor(), []);
  const menuInteractor = useMemo(() => new MenuInteractor(), []);
  const trimmedTreeName = treeName.trim();
  const compactTitleText = trimmedTreeName.length > 0 ? trimmedTreeName : 'SkillTrees';
  const toolbar = controlBarInteractor.getToolbarState({
    showSaveButton,
    isEditMode,
    treeId,
    isLoading,
    canEdit,
    hasUser: Boolean(user),
    hasSaveHandler: onSave != null,
    hasDeployHandler: onDeploy != null,
  });

  const handleLogoClick = () => {
    navigate('/about');
  };

  const handleTitleClick = () => {
    navigate('/about');
  };

  const handlePreviewEditToggle = () => {
    const target = menuInteractor.getModeToggleNavigationTarget(treeId, isEditMode);
    if (target) navigate(target);
  };

  return (
    <div className={`control-bar ${isNarrowViewport ? 'compact-edit-control-bar' : ''}`}>
      <div className="control-left">
        <img 
          src="/icons/big-logo.png" 
          alt="Skill Trees Logo" 
          className="logo clickable"
          onClick={handleLogoClick}
        />
        {isNarrowViewport ? (
          <AutoFitTitle
            text={compactTitleText}
            className="app-title compact-edit-app-title clickable"
            maxFontPx={42}
            minFontPx={11}
            onClick={handleTitleClick}
          />
        ) : (
          <span 
            className="app-title clickable"
            onClick={handleTitleClick}
          >
            Skill Trees
          </span>
        )}
      </div>
      
      <div className="control-right">
        {isNarrowViewport ? (
          <CompactEditMenu
            isEditMode={isEditMode}
            isDraft={isDraft}
            isLoading={isLoading}
            isSaving={isSaving}
            showSaveButton={showSaveButton}
            canEdit={canEdit}
            treeId={treeId}
            treeName={treeName}
            showRenameTreeInMenu={Boolean(
              isNarrowViewport && isEditMode && canEdit && !isLoading && onRenameTree,
            )}
            onRenameTree={onRenameTree}
            onSave={onSave}
            onDeploy={onDeploy}
            isDeploying={isDeploying}
          />
        ) : (
          <>
            {editStatus}

            {toolbar.showSaveButton && onSave && (
              <button
                onClick={onSave}
                className="btn btn-primary"
                disabled={isSaving}
              >
                {isSaving ? 'Publishing...' : 'Publish'}
              </button>
            )}

            {toolbar.showDeployButton && onDeploy && (
              <button
                onClick={onDeploy}
                className="btn btn-secondary"
                disabled={isDeploying}
                title="Download this tree as a self-contained folder you can host anywhere"
              >
                {isDeploying ? 'Deploying…' : 'Deploy'}
              </button>
            )}

            {toolbar.showPreviewEditToggle && (
              <button
                onClick={handlePreviewEditToggle}
                className="btn btn-secondary"
              >
                {toolbar.previewEditLabel}
              </button>
            )}

            <UserMenu />
          </>
        )}
      </div>
    </div>
  );
};
